import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, AlertCircle, X } from "lucide-react";
import { useAccess } from "../contexts/AccessContext";

const DISMISS_KEY = "vu_readonly_banner_dismissed";

/**
 * Subtle banner shown at the top of every dashboard when the user is in
 * Read-Only Mode (trial expired, cancelled, or past 48 h grace period).
 *
 * Never blocks any interaction — it just informs and offers the upgrade
 * path. Dismissible for the current session (sessionStorage) so power
 * users don't get nagged.
 *
 * A separate GRACE variant appears when a payment failed but the user is
 * still in the 48 h window — warmer copy, "update payment" CTA.
 */
export default function ReadOnlyBanner() {
    const { isReadOnly, isGrace, gracePeriodUntil } = useAccess();
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        try { setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1"); }
        catch { /* private mode */ }
    }, []);

    if (dismissed) return null;
    if (!isReadOnly && !isGrace) return null;

    const dismiss = () => {
        try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
        setDismissed(true);
    };

    if (isGrace) {
        // 48-hour warmup — no lockout, just a nudge to update the card.
        const hoursLeft = gracePeriodUntil
            ? Math.max(0, Math.ceil((new Date(gracePeriodUntil).getTime() - Date.now()) / 3_600_000))
            : null;
        return (
            <div
                data-testid="grace-banner"
                className="mb-4 rounded-2xl border border-[var(--brand-orange)]/40 bg-[var(--brand-orange)]/8 px-4 py-3 flex flex-wrap items-start gap-3"
            >
                <AlertCircle size={20} className="shrink-0 mt-0.5 text-[var(--brand-orange)]" />
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">
                        We&apos;re having trouble processing your payment.
                    </div>
                    <div className="text-xs text-[var(--text-ink)]/70 mt-0.5">
                        Your workspace remains fully available while you update your payment method.
                        {hoursLeft !== null && <> ~{hoursLeft} h left in the grace window.</>}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Link
                        to="/billing"
                        data-testid="grace-banner-cta"
                        className="btn-primary text-xs px-3"
                    >
                        Update Payment Method
                    </Link>
                    <button
                        onClick={dismiss}
                        data-testid="grace-banner-dismiss"
                        className="text-[var(--text-ink)]/50 hover:text-[var(--text-ink)] p-1"
                        aria-label="Dismiss"
                    ><X size={16} /></button>
                </div>
            </div>
        );
    }

    // Read-Only banner — the core "your workspace is safe" message.
    return (
        <div
            data-testid="readonly-banner"
            className="mb-4 rounded-2xl border border-[var(--brand-purple)]/30 bg-gradient-to-r from-[var(--brand-purple)]/6 to-[var(--brand-orange)]/6 px-4 py-3 flex flex-wrap items-start gap-3"
        >
            <div className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-[var(--brand-purple)]/15 grid place-items-center">
                <Lock size={16} className="text-[var(--brand-purple)]" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-sm flex items-center gap-2">
                    <span>Read-Only Mode</span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] font-semibold">
                        workspace safe
                    </span>
                </div>
                <div className="text-xs text-[var(--text-ink)]/70 mt-0.5 leading-relaxed">
                    Your free trial has ended. Your workspace is still completely safe and available to view.
                    Subscribe to continue creating, editing and using AI features.
                    <span className="text-[var(--text-ink)]/55"> Nothing you&apos;ve made is ever deleted.</span>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <Link
                    to="/billing"
                    data-testid="readonly-banner-cta"
                    className="btn-primary text-xs px-3"
                >
                    Upgrade Now
                </Link>
                <Link
                    to="/billing"
                    data-testid="readonly-banner-manage"
                    className="btn-ghost text-xs px-2 hidden sm:inline-flex"
                >
                    Manage Subscription
                </Link>
                <button
                    onClick={dismiss}
                    data-testid="readonly-banner-dismiss"
                    className="text-[var(--text-ink)]/50 hover:text-[var(--text-ink)] p-1"
                    aria-label="Dismiss"
                ><X size={16} /></button>
            </div>
        </div>
    );
}
